import { useEffect, useState } from 'react';
import { Cell, List, Section, Spinner } from '@telegram-apps/telegram-ui';
import type { OverviewResponse, KFactorResponse } from '@veylme/shared';

import { getAnalyticsK, getAnalyticsOverview } from '../api/client.js';
import { useBackButton } from '../lib/nativeUi.js';

/**
 * AnalyticsScreen — панель посещаемости (admin-only).
 *
 * Под-поток: не входит в TAB_SCREENS, Tabbar скрыт.
 * Назад → SETTINGS через нативный BackButton.
 *
 * Тексты захардкожены по-русски: это внутренний экран владельца,
 * трогать 27 i18n-бандлов не требуется.
 *
 * work-028 (fix plan-verifier БЛОКЕР-3): ДВА независимых загрузчика —
 * overview и K-фактор грузятся раздельными useEffect. Единого early-return
 * на ошибку НЕТ: экран всегда рендерит <List>, каждая группа секций
 * деградирует независимо («—»/короткая пометка), сбой одной загрузки не
 * гасит секции другой.
 */
export interface AnalyticsScreenProps {
  onBack: () => void;
}

type OverviewState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'loaded'; data: OverviewResponse };

type KState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'loaded'; data: KFactorResponse };

/** Человекочитаемые подписи для 8 типов событий. */
const EVENT_LABELS: Record<string, string> = {
  app_opened: 'Открытий приложения',
  box_created: 'Создано ящиков',
  invite_shared_to_story: 'Поделились приглашением в сторис',
  ask_opened: 'Открытий экрана вопроса',
  question_sent: 'Отправлено вопросов',
  answer_created: 'Написано ответов',
  answer_shared_to_story: 'Поделились ответом в сторис',
  chat_shared: 'Поделились в чат',
};

export function AnalyticsScreen({ onBack }: AnalyticsScreenProps) {
  const [overviewState, setOverviewState] = useState<OverviewState>({ kind: 'loading' });
  const [kState, setKState] = useState<KState>({ kind: 'loading' });

  useBackButton(onBack);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAnalyticsOverview();
        if (!cancelled) setOverviewState({ kind: 'loaded', data });
      } catch {
        if (!cancelled) setOverviewState({ kind: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAnalyticsK();
        if (!cancelled) setKState({ kind: 'loaded', data });
      } catch {
        if (!cancelled) setKState({ kind: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (overviewState.kind === 'loading' && kState.kind === 'loading') {
    return (
      <List>
        <Section>
          <Cell>
            <Spinner size="m" />
          </Cell>
        </Section>
      </List>
    );
  }

  const fmt = (n: number) => n.toLocaleString('ru-RU');
  const pct = (part: number, total: number) =>
    total > 0 ? `${((part / total) * 100).toFixed(1)}%` : '—';

  return (
    <List>
      {overviewState.kind === 'loading' && (
        <Section header="Пользователи">
          <Cell>
            <Spinner size="s" />
          </Cell>
        </Section>
      )}

      {overviewState.kind === 'error' && (
        <Section header="Пользователи">
          <Cell description="Не удалось загрузить данные. Попробуйте позже.">
            Ошибка загрузки
          </Cell>
        </Section>
      )}

      {overviewState.kind === 'loaded' && (
        <>
          <Section header="Пользователи">
            <Cell after={fmt(overviewState.data.totalUsers)}>Всего</Cell>
            <Cell after={fmt(overviewState.data.newUsers.d1)}>Новые за 1 день</Cell>
            <Cell after={fmt(overviewState.data.newUsers.d7)}>Новые за 7 дней</Cell>
            <Cell after={fmt(overviewState.data.newUsers.d30)}>Новые за 30 дней</Cell>
          </Section>

          <Section
            header="Активные авторы"
            footer="Автор, совершивший действие, включая заход; окна накопительные, вложены"
          >
            <Cell after={fmt(overviewState.data.activeAuthors.d1)}>За 1 день</Cell>
            <Cell after={fmt(overviewState.data.activeAuthors.d7)}>За 7 дней</Cell>
            <Cell after={fmt(overviewState.data.activeAuthors.d30)}>За 30 дней</Cell>
          </Section>

          <Section
            header="Заходы анонимов"
            footer="Открытий экрана вопроса; это объём событий, не уникальные люди"
          >
            <Cell after={fmt(overviewState.data.anonAskOpens.d1)}>За 1 день</Cell>
            <Cell after={fmt(overviewState.data.anonAskOpens.d7)}>За 7 дней</Cell>
            <Cell after={fmt(overviewState.data.anonAskOpens.d30)}>За 30 дней</Cell>
          </Section>

          <Section header="Активность за 30 дней">
            {(Object.entries(overviewState.data.activity30d) as [string, number][]).map(
              ([type, count]) => (
                <Cell key={type} after={fmt(count)}>
                  {EVENT_LABELS[type] ?? type}
                </Cell>
              ),
            )}
          </Section>
        </>
      )}

      {kState.kind === 'loading' && (
        <Section header="Вирусность (K-фактор)">
          <Cell>
            <Spinner size="s" />
          </Cell>
        </Section>
      )}

      {kState.kind === 'error' && (
        <Section header="Вирусность (K-фактор)">
          <Cell description="Не удалось загрузить данные. Попробуйте позже.">
            Ошибка загрузки
          </Cell>
        </Section>
      )}

      {kState.kind === 'loaded' && (
        <>
          <Section
            header="Вирусность (K-фактор)"
            footer="K меньше 1 — каждый приводит меньше одного нового; больше 1 — вирусный рост"
          >
            <Cell after={kState.data.kFactor.toFixed(3)}>K-фактор</Cell>
            <Cell after={fmt(kState.data.newAttributedAuthors)}>Новых по приглашениям</Cell>
            <Cell after={fmt(kState.data.activeAuthors)}>Активных авторов</Cell>
          </Section>

          <Section header="Каналы привлечения">
            <Cell after={fmt(kState.data.channelBreakdown.story)}>Через сторис</Cell>
            <Cell after={fmt(kState.data.channelBreakdown.chat)}>Через чат</Cell>
            <Cell after={fmt(kState.data.channelBreakdown.direct)}>Прямая ссылка</Cell>
          </Section>
        </>
      )}

      {overviewState.kind === 'loading' && (
        <Section header="Вопросы и ответы (за всё время)">
          <Cell>
            <Spinner size="s" />
          </Cell>
        </Section>
      )}

      {overviewState.kind === 'error' && (
        <Section header="Вопросы и ответы (за всё время)">
          <Cell description="Не удалось загрузить данные. Попробуйте позже.">
            Ошибка загрузки
          </Cell>
        </Section>
      )}

      {overviewState.kind === 'loaded' && (
        <>
          <Section header="Вопросы и ответы (за всё время)">
            <Cell after={fmt(overviewState.data.content.totalQuestions)}>Всего вопросов</Cell>
            <Cell after={fmt(overviewState.data.content.totalAnswers)}>Всего ответов</Cell>
            <Cell
              after={pct(
                overviewState.data.content.totalAnswers,
                overviewState.data.content.totalQuestions,
              )}
            >
              Отвечено
            </Cell>
            <Cell after={fmt(overviewState.data.content.questionStatus.pending)}>
              В ожидании
            </Cell>
            <Cell after={fmt(overviewState.data.content.questionStatus.hidden)}>Скрыто</Cell>
            <Cell after={fmt(overviewState.data.content.questionStatus.removed)}>Удалено</Cell>
            <Cell after={fmt(overviewState.data.content.flagged)}>Помечено фильтром</Cell>
            <Cell after={fmt(overviewState.data.content.bypassed)}>Прошло без проверки</Cell>
          </Section>

          <Section header="Аудитория">
            <Cell after={fmt(overviewState.data.audience.premium)}>Telegram Premium</Cell>
            <Cell after={fmt(overviewState.data.audience.notificationsOn)}>
              Включили уведомления
            </Cell>
            {overviewState.data.audience.languages.map((lang) => (
              <Cell key={lang.code} after={fmt(lang.count)}>
                {lang.code}
              </Cell>
            ))}
            {overviewState.data.audience.otherLanguages > 0 && (
              <Cell after={fmt(overviewState.data.audience.otherLanguages)}>
                Другие языки
              </Cell>
            )}
          </Section>

          <Section>
            <Cell
              description={`Сводка сгенерирована: ${new Date(overviewState.data.generatedAt).toLocaleString('ru-RU')}`}
            />
          </Section>
        </>
      )}
    </List>
  );
}
